'use client';
import * as React from 'react';

/**
 * Fetches the taken slots for a given ISO date (YYYY-MM-DD) from the public
 * availability endpoint. Returns the set of taken HH:mm times plus loading
 * state. Used by the booking form, but reusable in any custom scheduler UI.
 */
export function useAvailability(dateIso: string) {
  const [takenIso, setTakenIso] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!dateIso) return;
    let cancelled = false;
    setLoading(true);
    fetch(`/api/appointments/availability?date=${dateIso}`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setTakenIso(d.taken ?? []);
      })
      .catch(() => {
        if (!cancelled) setTakenIso([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateIso]);

  // Convenience: taken times as HH:mm in the local timezone.
  const takenTimes = React.useMemo(
    () => new Set(takenIso.map((iso) => new Date(iso).toISOString().slice(11, 16))),
    [takenIso],
  );

  return { takenIso, takenTimes, loading };
}
